import React, { useContext, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
//import config from "./ScanConfig";
//import BarcodeScannerComponent from "react-usb-barcode-scanner";

import Header from './components/Header/Header';
import Login from './views/Login';
import Salgs from './views/Salgs';
import SisteSalgs from './views/SisteSalgs';
import UserLogin from './views/UserLogin/UserLogin';
import ApneBilleter from './views/ApneBilleter/ApneBilleter';
import BilletHistorikk from './views/BilletHistorikk';
import Billettliste from './views/Billettliste';
import SisteTransaksjoner from './views/SisteTransaksjoner';
import SisteKortTransaksjoner from './views/SisteKortTransaksjoner';
import AvslutteSkiftet from './views/AvslutteSkiftet/AvslutteSkiftet';
import Spillplan from './views/Spillplan/Spillplan';

import LoginKiosk from './views/LoginKiosk';
import SalgsKiosk from './views/SalgsKiosk/SalgsKiosk';

import ModalSpecialGames from './views/SpecialGames/ModalSpecialGames';
import ModalEndShiftAuto from './views/AvslutteSkiftet/ModalEndShiftAuto';
// Stage-only test data; delete with views/Dev/TestTransactions.js
import TestTransactions from './views/Dev/TestTransactions';

import { Grid } from '@mui/material';
import { AppContext } from './context/Context';
import { SocketContext } from './context/SocketContext';
import ModalDisconnect from './views/SocketStatusModals/ModalDisconnect';
import ModalReconnect from './views/SocketStatusModals/ModalReconnect';
import LoadingScreen from './views/Loading/Loading';
import { takeFullScreenshot, checkLocalServer } from './utils/screenshots';

import { ThemeProvider } from '@mui/material/styles';
import { kioskTheme } from './theme/theme';

// A blip shorter than this never reaches the operator; nothing is lost in it
const RECONNECT_NOTICE_AFTER_MS = 5000;

export default function App() {
  const location = useLocation();

  const {
    isLogin,
    showSpecialGames,
    showModalEndShift,
    updateEnviroment,
    isDisconnected,
    updateIsDisconnected,
    isReconnected,
    updateIsReconnected,
    isLoadingScreen,
    isSelfServiceKiosk,
  } = useContext(AppContext);

  const isDisconnectedRef = useRef(isDisconnected);
  useEffect(() => {
    isDisconnectedRef.current = isDisconnected;
  }, [isDisconnected]);

  const showSpecialGamesRef = useRef(showSpecialGames);
  useEffect(() => {
    showSpecialGamesRef.current = showSpecialGames;
  }, [showSpecialGames]);

  const isSelfServiceKioskRef = useRef(isSelfServiceKiosk);
  useEffect(() => {
    isSelfServiceKioskRef.current = isSelfServiceKiosk;
  }, [isSelfServiceKiosk]);

  useEffect(() => {
    const hostname = window.location.hostname;

    if (hostname === 'localhost') {
      updateEnviroment('stage01');
    } else if (hostname.includes('stage01')) {
      updateEnviroment('stage01');
    } else if (hostname.includes('stage02')) {
      updateEnviroment('stage02');
    } else {
      updateEnviroment('prod');
    }
  }, []);

  // The first connection of the page load is not a reconnection
  const hasConnectedRef = useRef(false);
  const disconnectedAtRef = useRef(null);

  const { socket } = useContext(SocketContext);
  useEffect(() => {
    socket.on('connect', () => {
      const wasDown = isDisconnectedRef.current;
      const downFor = disconnectedAtRef.current
        ? Date.now() - disconnectedAtRef.current
        : 0;

      disconnectedAtRef.current = null;

      if (wasDown) {
        updateIsDisconnected(false);
        // Only a line that was really down, and long enough to matter, is announced
        if (hasConnectedRef.current && downFor >= RECONNECT_NOTICE_AFTER_MS) {
          updateIsReconnected(true);
        }
      }

      hasConnectedRef.current = true;
    });

    socket.on('disconnect', () => {
      if (!disconnectedAtRef.current) {
        disconnectedAtRef.current = Date.now();
      }
      updateIsDisconnected(true);
      updateIsReconnected(false);
    });

    socket.on('connect_error', () => {
      if (!disconnectedAtRef.current) {
        disconnectedAtRef.current = Date.now();
      }
      updateIsDisconnected(true);
      updateIsReconnected(false);
    });

    return () => {
      socket.off('connect');
      socket.off('disconnect');
      socket.off('connect_error');
    };
  }, [socket]);

  // Probe the screenshot server on mount
  useEffect(() => {
    checkLocalServer();
  }, []);

  const screenShot = useCallback(
    () => requestAnimationFrame(takeFullScreenshot),
    []
  );

  useEffect(() => {
    window.addEventListener('pointerdown', screenShot, { passive: true });
    return () => window.removeEventListener('pointerdown', screenShot);
  }, [screenShot]);

  return (
    <>
      {isLoadingScreen && <LoadingScreen />}

      <Grid
        container
        direction="row"
        sx={{
          height: '100vh',
          backgroundColor: '#08141e',
        }}
      >
        {isDisconnected && location.pathname !== '/user-login' && (
          <ModalDisconnect />
        )}
        {isReconnected && location.pathname !== '/user-login' && (
          <ModalReconnect />
        )}

        {showModalEndShift && <ModalEndShiftAuto />}

        {isLogin && <TestTransactions />}

        {isLogin && !isSelfServiceKioskRef.current && (
          <Grid item xs={2}>
            <Header />
          </Grid>
        )}
        <Grid
          item
          xs={!isLogin ? 12 : 10}
          sx={{ height: '100%', backgroundColor: '#15192b' }}
        >
          {showSpecialGames && <ModalSpecialGames />}
          <Routes>
            <Route path="/" element={<Login />} />
            <Route path="/salgs" element={<Salgs />} />
            <Route path="/siste-salgs" element={<SisteSalgs />} />
            <Route path="/apne-billeter" element={<ApneBilleter />} />
            <Route path="/billet-historikk" element={<BilletHistorikk />} />
            <Route path="/billett-liste" element={<Billettliste />} />
            <Route path="/avslutte-skiftet" element={<AvslutteSkiftet />} />
            <Route
              path="/siste-transaksjoner"
              element={<SisteTransaksjoner />}
            />
            <Route
              path="/kort-transaksjoner"
              element={<SisteKortTransaksjoner />}
            />
            <Route path="/login" element={<Login />} />
            <Route exact path="/user-login" element={<UserLogin />} />
            <Route
              exact
              path="/avslutte-skiftet"
              element={<AvslutteSkiftet />}
            />
            <Route exact path="/spillplan" element={<Spillplan />} />

            <Route
              path="/login-kiosk"
              element={
                <ThemeProvider theme={kioskTheme}>
                  <LoginKiosk />
                </ThemeProvider>
              }
            />
            <Route
              path="/salgs-kiosk"
              element={
                <ThemeProvider theme={kioskTheme}>
                  <SalgsKiosk />
                </ThemeProvider>
              }
            />
          </Routes>
        </Grid>
      </Grid>
    </>
  );
}
